import { Image, Input, Table } from 'antd';
import { TableProps } from 'antd/lib';
import classNames from 'classnames';
import { useMemo, useState } from 'react';

import { PlusCircleFilled } from '@ant-design/icons';

import * as St from './CustomLaborModal.styles';
import {
  CustomLaborFieldErrors,
  CustomLaborModalProps,
  CustomOptionDetail,
} from './CustomLaborModal.types';

import {
  CUSTOM_WORK_MODAL_MAX_QUANTITY,
  IMAGE_RED_TRASH_CAN,
} from '~/core/constants';
import useResetableState from '~/core/hooks/useResetableState';
import {
  formatPriceUnitOrFree,
  getProductDescription,
  imgUrl,
} from '~/core/utils';
import { useAppSelector } from '~/redux/hooks';
import {
  type CustomLaborData,
  selectPickedCustomLaborOptionalItems,
} from '~/redux/slices/optionalItems';

/**
 * We do a check against this price so needs to be consistent
 */
const INITIAL_PRICE = '0';

/**
 * Set a limit of 255 characters for notes
 */
const MAX_CHARS_FOR_NOTES = 255;

const CustomLaborModal = ({
  customLabor,
  onConfirm,
  onCancel,
  ...props
}: CustomLaborModalProps) => {
  const [validationErrors, setValidationErrors] = useState<
    Record<number, CustomLaborFieldErrors>
  >({});

  const [
    customLaborCandidate,
    setCustomLaborCandidate,
    resetCustomLaborCandidate,
  ] = useResetableState<CustomLaborData[] | undefined>(undefined);

  const resetCandidateAndCancel = () => {
    resetCustomLaborCandidate();
    setValidationErrors({}); // clear validation errors on cancel
    onCancel?.();
  };

  const _selectedCustomLaborOptionalItems = useAppSelector(
    selectPickedCustomLaborOptionalItems
  );
  const customLaborId = customLabor?.productId;

  /**
   *  The specific items selected for this particular custom labor product
   */
  const selectedCustomLabor = customLaborId
    ? _selectedCustomLaborOptionalItems?.[customLaborId]
    : undefined;

  const emptyRow: CustomLaborData | undefined = useMemo(
    () =>
      customLaborId
        ? {
            quantity: 0,
            notes: '',
            productId: customLaborId,
          }
        : undefined,
    [customLaborId]
  );

  /**
   * The selected custom labor optional items with additional empty row
   */
  const selectedCustomLaborWithEmptyRow: CustomLaborData[] | undefined =
    useMemo(
      () =>
        selectedCustomLabor && emptyRow
          ? [...selectedCustomLabor, emptyRow].filter(Boolean)
          : undefined,
      [selectedCustomLabor, emptyRow]
    );

  // Start with three empty rows
  const initialCandidateCustomLabor: CustomLaborData[] = (
    emptyRow ? Array.from({ length: 3 }).map(() => emptyRow) : []
  ) satisfies CustomLaborData[];

  /**
   * The custom labor optional items in this modal. It may be local state, redux or a default initial value
   */
  const _customLaborOptionalItems: CustomLaborData[] = useMemo(() => {
    // isSelectedCustomLaborValid is true if the selectedCustomLaborWithEmptyRow is defined and has at least one non-empty row
    const isSelectedCustomLaborValid =
      selectedCustomLaborWithEmptyRow &&
      selectedCustomLaborWithEmptyRow.filter(
        (item) => item.quantity && item.quantity > 0
      ).length > 0;

    return (
      customLaborCandidate ??
      (isSelectedCustomLaborValid
        ? selectedCustomLaborWithEmptyRow
        : initialCandidateCustomLabor)
    );
  }, [
    customLaborCandidate,
    selectedCustomLaborWithEmptyRow,
    initialCandidateCustomLabor,
  ]);

  const customLaborOptionalItemPrice = customLabor?.price ?? 0;

  const addRow = () => {
    if (emptyRow) {
      setCustomLaborCandidate([...(_customLaborOptionalItems ?? []), emptyRow]);
    }
  };

  const addToOptionalItemsState = () => {
    const errors: Record<number, CustomLaborFieldErrors> = {};

    _customLaborOptionalItems.forEach((item, index) => {
      const hasQuantity = !!item.quantity;
      const hasNotes = !!item.notes;

      if (hasQuantity !== hasNotes) {
        errors[index] = {};
        if (!hasQuantity) {
          errors[index].quantity = 'Please enter a quantity';
        }
        if (!hasNotes) {
          errors[index].notes = 'Please enter notes';
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);

      return;
    }

    setValidationErrors({});
    onConfirm?.(
      _customLaborOptionalItems.filter((item) => (item.quantity ?? 0) > 0)
    );
    resetCustomLaborCandidate();
  };

  /**The data to generate the rows shown in the table */
  const data: CustomOptionDetail[] = useMemo(
    () =>
      Object.values(_customLaborOptionalItems).map<CustomOptionDetail>(
        (item) => ({
          quantity: item.quantity ? `${item.quantity}` : '',
          notes: item.notes,
          price:
            customLaborOptionalItemPrice && item.quantity
              ? `${customLaborOptionalItemPrice * item.quantity}`
              : INITIAL_PRICE,
        })
      ),
    [_customLaborOptionalItems, customLaborOptionalItemPrice]
  );

  const customLaborTotal = useMemo(
    () => data.reduce((acc, next) => acc + +next.price, 0),
    [data]
  );

  const onChangePickedCustomLaborOptionalItemProperty = <
    Key extends keyof CustomLaborData,
  >(
    key: Key,
    value: CustomLaborData[Key],
    index: number
  ): void => {
    setValidationErrors({});
    setCustomLaborCandidate(
      _customLaborOptionalItems.with(index, {
        ..._customLaborOptionalItems[index],
        [key]: value,
      })
    );
  };

  const removeRow = (index: number) => {
    setCustomLaborCandidate(_customLaborOptionalItems.toSpliced(index, 1));
  };

  const columns: TableProps<CustomOptionDetail>['columns'] = [
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (text: string, record, index) => {
        const quantityErrorMsg = validationErrors[index]?.quantity;

        return (
          <div>
            <St.TableInput
              key={`quantity-${index}`}
              placeholder="0"
              className={classNames({
                'zero-price': record.price === INITIAL_PRICE,
                'has-error': !!quantityErrorMsg,
              })}
              type="number"
              inputMode="numeric"
              min={0}
              value={text}
              onKeyDown={(e) => {
                if (
                  e.key === '-' ||
                  e.key === '+' ||
                  e.key === '.' ||
                  e.key === 'e'
                ) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const raw = e.target.valueAsNumber;
                const safe = Number.isFinite(raw) ? raw : 0;

                const next = Math.min(
                  CUSTOM_WORK_MODAL_MAX_QUANTITY,
                  Math.max(0, Math.floor(safe))
                );

                onChangePickedCustomLaborOptionalItemProperty(
                  'quantity',
                  next,
                  index
                );
              }}
            />
            {quantityErrorMsg && (
              <St.ErrorMessage>{quantityErrorMsg}</St.ErrorMessage>
            )}
          </div>
        );
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (text, _, index) => {
        const notesErrorMsg = validationErrors[index]?.notes;

        return (
          <div>
            <Input.TextArea
              key={`notes-${index}`}
              placeholder="Enter custom labor description..."
              className={classNames('labor_notes', {
                'has-error': !!notesErrorMsg,
              })}
              value={text}
              onChange={(e) =>
                onChangePickedCustomLaborOptionalItemProperty(
                  'notes',
                  e.target.value.slice(0, MAX_CHARS_FOR_NOTES),
                  index
                )
              }
            />
            {notesErrorMsg && (
              <St.ErrorMessage>{notesErrorMsg}</St.ErrorMessage>
            )}
          </div>
        );
      },
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: string) => (
        <St.TablePriceLabel
          className={classNames({
            'zero-price': price === INITIAL_PRICE,
          })}
        >
          {formatPriceUnitOrFree(Number(price))}
        </St.TablePriceLabel>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_: string, __, index: number) => (
        <St.RemoveOptionButton onClick={() => removeRow(index)}>
          <Image preview={false} src={imgUrl(IMAGE_RED_TRASH_CAN)} />
        </St.RemoveOptionButton>
      ),
    },
  ];

  return (
    <St.CustomLaborModal
      {...props}
      open={!!customLabor}
      onCancel={resetCandidateAndCancel}
      footer={
        <St.ButtonsContainer>
          <St.AddRowButton type="link" onClick={addRow}>
            <St.AddRowButtonContentWrapper>
              <PlusCircleFilled /> Add Row
            </St.AddRowButtonContentWrapper>
          </St.AddRowButton>
          <St.AddButton onClick={addToOptionalItemsState}>Add</St.AddButton>
          <St.Button onClick={resetCandidateAndCancel}>Cancel</St.Button>
        </St.ButtonsContainer>
      }
    >
      <St.CustomLaborModalTitle>
        {customLabor ? getProductDescription(customLabor) : ''}
      </St.CustomLaborModalTitle>
      <St.TotalLabel>
        {`Total ${formatPriceUnitOrFree(customLaborTotal)}`}
      </St.TotalLabel>

      <Table pagination={false} columns={columns} dataSource={data} />
    </St.CustomLaborModal>
  );
};

export default CustomLaborModal;
